<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import { watchDebounced } from '@vueuse/core'
import GTag from '@consumer/components/GTag/GTag.vue'
import { useConciergeState } from './conciergeState'
import { parseSignals, type SignalParser } from './parseSignals'
import { budgetLabel } from './signalDictionary'
import { useTypewriterPlaceholder } from './useTypewriterPlaceholder'
import { nextSignalHint, recommendationReadiness } from './recommendationRequest'
import { trackConciergeEvent } from './conciergeEventTracking'
import { useConciergeTakeover } from './useConciergeTakeover'

// The parser is injectable so an alternate implementation (a fuller word list,
// later an LLM) swaps in without touching this component. This component owns the
// input, live signal parsing, and the detected-signal chips; the results surface
// is mounted separately (the rich concierge island).
const props = defineProps<{
  parser?: SignalParser
}>()

const state = useConciergeState()
const parse = props.parser ?? parseSignals

const description = _ref(state.raw)

// TEMPORARY (concept-proving): hardcoded rotating placeholder examples. Final
// copy is a content decision, not these literals.
const EXAMPLES = [
  'a birthday gift for my sister in Seattle who loves sushi',
  "something for my coworker who's really into cooking",
  "a retirement gift for my dad who's into golf and jazz",
  'an anniversary present for my wife in New York',
]
const placeholder = (useTypewriterPlaceholder(EXAMPLES, () => !description.value.trim(), { holdMs: 5000 }))

const readiness = _computed(() => recommendationReadiness(state.signals))
const hint = _computed(() => nextSignalHint(state.signals))

const takeover = useConciergeTakeover()

let interacted = false
// The takeover teleport blurs then re-focuses this field on engage; dedupe so that restore
// doesn't log a second input_focus (re-engagement shows in takeover_engage).
let focusTracked = false
let fieldFocused = _ref(false)
function onFocus () {
  fieldFocused.value = true
  if (focusTracked) return
  focusTracked = true
  trackConciergeEvent('input_focus', { variant: 'free_text' })
}
function onBlur () {
  fieldFocused.value = false
}
function onInput () {
  takeover?.markEditing()
  if (interacted) return
  interacted = true
  trackConciergeEvent('first_interaction', { variant: 'free_text' })
}

const stopReporting = takeover?.registerInput({
  active: () => fieldFocused.value,
  empty: () => !description.value.trim(),
  dismissOnOutsideClick: () => !description.value.trim(),
})
onUnmounted(() => stopReporting?.())

type DetectedChip = { key: string, label: string, emoji: string }

// TEMPORARY (concept-proving): interests use one placeholder glyph. In production
// each interest carries its own GiftTag icon_url — the endpoint already returns it.
const INTEREST_GLYPH = '✨'

// Chips echo the buyer's own wording where the parser kept it, so someone who
// typed "husband" isn't shown "spouse". The canonical still drives everything
// downstream; this is display only.
const detected = _computed<DetectedChip[]>(() => {
  const { relationship, occasion, location, interests, budget } = state.signals
  const chips: DetectedChip[] = []
  if (relationship.value)
    chips.push({ key: `r-${relationship.value}`, label: relationship.display ?? relationship.value, emoji: '🧑' })
  if (occasion.value)
    chips.push({ key: `o-${occasion.value}`, label: occasion.display ?? occasion.value, emoji: '🎉' })
  if (location.value)
    chips.push({ key: `l-${location.value}`, label: location.display ?? location.value, emoji: '📍' })
  for (const interest of interests.value)
    chips.push({ key: `i-${interest}`, label: interest, emoji: INTEREST_GLYPH })
  if (budget.value) chips.push({ key: 'budget', label: budgetLabel(budget.value), emoji: '💰' })
  return chips
})

const announcement = _computed(() => {
  if (!detected.value.length) return ''
  return readiness.value.ready
    ? `${readiness.value.filledCount} details detected. Ready to find gifts.`
    : readiness.value.reason
})

// Reacts as the buyer types: re-read the whole field and refresh the detected
// signals in the store (the results surface watches those signals and fetches).
// The seq guard drops a slow parse superseded by a newer keystroke (matters once
// an async parser is injected), so chips never show signals from text the buyer
// already edited away.
let parseSeq = 0
watchDebounced(
  () => description.value,
  async (raw) => {
    const seq = ++parseSeq
    state.raw = raw
    const { signals, surfaces } = await parse(raw)
    if (seq !== parseSeq) return
    state.replaceFromParse(signals, surfaces)
  },
  { debounce: 220, immediate: true },
)
</script>

<template>
  <div class="concierge-input">
    <label for="concierge-description">What are you shopping for?</label>
    <div class="input-wrap">
      <div class="input-inner">
        <input
          id="concierge-description"
          v-model="description"
          type="text"
          autocomplete="off"
          :placeholder="placeholder"
          @focus="onFocus"
          @blur="onBlur"
          @input="onInput"
        >
      </div>
    </div>

    <TransitionGroup
      v-if="detected.length"
      tag="ul"
      name="chip"
      class="detected"
      aria-label="Detected details"
    >
      <li v-for="chip in detected" :key="chip.key">
        <GTag size="xs" variant="unselected" :emoji="chip.emoji" :label="chip.label"/>
      </li>
    </TransitionGroup>

    <p v-if="detected.length && hint" class="request-hint">{{ hint }}</p>

    <p class="sr-only" role="status" aria-live="polite">{{ announcement }}</p>
  </div>
</template>

<style lang="scss" scoped>
@use "./conciergeRainbow" as rainbow;

// Caps its own column: the takeover surface spans the page's content width for
// the results, so the input centers itself at a readable width within it.
.concierge-input {
  display: grid;
  gap: 8px;
  margin: 0 auto;
  max-width: 620px;
}

label {
  font-size: 18px;
  font-weight: 700;
  text-align: center;
}

// The rainbow lives on the wrapper as a 2px gradient frame; the white inner box
// masks it so only the border shows. It drifts slowly and warms up on focus.
.input-wrap {
  animation: concierge-rainbow-pan 8s linear infinite;
  background: rainbow.$gradient;
  background-size: 200% 100%;
  border-radius: 16px;
  padding: 2px;

  &:focus-within {
    animation-duration: 3s;
    filter: saturate(1.3);
  }
}

@media (prefers-reduced-motion: reduce) {
  .input-wrap {
    animation: none;
  }
}

.input-inner {
  background: white;
  border-radius: 14px;
}

input {
  background: transparent;
  border: 0;
  border-radius: 14px;
  box-sizing: border-box;
  font: inherit;
  font-size: 16px;
  letter-spacing: normal;
  line-height: 1.4;
  padding: 14px 16px;
  width: 100%;

  &::placeholder {
    color: var(--color-text-tertiary);
  }

  &:focus-visible {
    outline: none;
  }
}

.detected {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
}

.chip-enter-active,
.chip-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.chip-enter-from,
.chip-leave-to {
  opacity: 0;
  transform: scale(0.85);
}

.request-hint {
  color: var(--color-text-tertiary);
  font-size: 13px;
  margin: 4px 0 0;
}
</style>
